import { ControllerRenderProps } from 'react-hook-form'

import { TCommonFields } from '../../types'
import { FileFieldRow, FileInput } from './file-input'

const CHUNK_SIZE = 20 * 1024 * 1024 // 20MB

export const uploadStreamingFile = async <T,>(field: TStreamingFileField<T>, file: File, id: string): Promise<void> => {
    // an empty file is still sent as one (empty) chunk
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE))

    field.onStart?.()

    for (let chunk = 0; chunk < totalChunks; chunk++) {
        const offset = chunk * CHUNK_SIZE
        const chunkSize = Math.min(CHUNK_SIZE, file.size - offset)
        const blob = file.slice(offset, offset + chunkSize)

        const chunkFormData = new FormData()
        chunkFormData.append('chunk', blob)
        chunkFormData.append('currentChunk', chunk.toString())
        chunkFormData.append('lastChunk', (totalChunks - 1).toString())
        chunkFormData.append('id', id)

        const response = await fetch(field.url, {
            method: 'POST',
            body: chunkFormData,
        })

        if (!response.ok) {
            field.onError?.()
            throw new Error(`Chunk ${chunk} upload failed`)
        }

        field.onProgress?.((chunk + 1) / totalChunks)
    }

    field.onCompleted?.()
}

export type TStreamingFileField<T> = TCommonFields<T> & {
    type: 'streaming-file'
    props?: Record<string, any>
    /** chunks are posted here together with the `id` returned by the form's `onSubmit` */
    url: string
    onStart?: () => void,
    onProgress?: (progress: number) => void,
    onError?: () => void,
    onCompleted?: () => void,
}

type TStreamingFileFieldProps<T> = {
    field: ControllerRenderProps<any, string>
    field_: TStreamingFileField<T>
    required: boolean
    disabled: boolean
    /** @deprecated the stored file name is taken from the form value */
    item?: string
}

export const StreamingFileField = <T,>({ field, field_, required, disabled }: TStreamingFileFieldProps<T>) => (
    <FileInput field={field} rowField={field_} required={required} disabled={disabled} />
)

type TProps<T> = {
    rowField: TStreamingFileField<T>
    fieldName: string
    labelWidth?: string
    /** @deprecated the stored file name is taken from the form value */
    fieldItem?: string
}

const StreamingFileFieldDefault = <T,>({ fieldName, rowField, labelWidth }: TProps<T>) => (
    <FileFieldRow fieldName={fieldName} rowField={rowField} labelWidth={labelWidth} />
)

export default StreamingFileFieldDefault
